import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import {
  MagnifyingGlass as Search,
  Sliders as SlidersHorizontal,
  CaretLeft as ChevronLeft,
  CaretRight as ChevronRight,
  CaretDown as ChevronDown,
  ArrowsDownUp as ChevronsUpDown,
  UserPlus,
  X,
  Users as UsersIcon,
} from '@phosphor-icons/react'
import { supabase } from '@/lib/supabase'
import type { AsociadoNeoRow, EstadoAsociado, KycEstado, TipoPersona } from '@/lib/typesNeoCore'

const PAGE_SIZE = 10

/** Prospectos con más de estos días sin comprobante caen a "En frío". */
const EN_FRIO_DIAS = 14

type SortKey = 'primer_nombre' | 'no_asociado' | 'fecha_ingreso' | 'created_at'
type SortDir = 'asc' | 'desc'

/**
 * Las tabs mapean a queries del RPC:
 *   - 'todos'      → sin filtro de estado
 *   - 'nuevos'     → prospecto + pendiente (necesitan aprobación del operador)
 *   - 'activos'    → solo activos
 *   - 'suspendidos'→ solo suspendidos
 *   - 'retirados'  → solo retirados
 */
type TabKey = 'todos' | 'nuevos' | 'activos' | 'suspendidos' | 'retirados'

const TAB_TO_ESTADOS: Record<TabKey, EstadoAsociado[] | null> = {
  todos: null,
  nuevos: ['prospecto', 'pendiente'],
  activos: ['activo'],
  suspendidos: ['suspendido'],
  retirados: ['retirado'],
}

/** Row del RPC contar_asociados_por_estado. */
type CountsRow = {
  total: number | string
  prospecto: number | string
  pendiente: number | string
  activo: number | string
  suspendido: number | string
  retirado: number | string
}

const VALID_ESTADOS = new Set<EstadoAsociado>([
  'prospecto',
  'pendiente',
  'activo',
  'suspendido',
  'retirado',
])

/**
 * Lee el query param `?tab=` (nuevo) o `?estado=` (legacy, retro-compatible).
 * El banner global usaba `?estado=pendiente` — hoy debería usar `?tab=nuevos`
 * pero mantenemos el mapeo por si algún link viejo circula.
 *
 * Default = 'activos': la mayoría de las veces el operador entra a operar
 * sobre el padrón real (contactar, gestionar productos), y los nuevos ya
 * se avisan por el banner global. Todos/Nuevos quedan a 1 clic.
 */
function parseTabFromQuery(qs: URLSearchParams): TabKey {
  const tab = (qs.get('tab') ?? '').toLowerCase()
  if (tab === 'nuevos' || tab === 'todos' || tab === 'activos' ||
      tab === 'suspendidos' || tab === 'retirados') {
    return tab
  }
  const legacy = (qs.get('estado') ?? '').toLowerCase()
  if (legacy === 'prospecto' || legacy === 'pendiente') return 'nuevos'
  if (legacy === 'activo') return 'activos'
  if (legacy === 'suspendido') return 'suspendidos'
  if (legacy === 'retirado') return 'retirados'
  return 'activos'
}

/** Compat: helper viejo que otras vistas pueden llamar. */
function parseEstadoFromQuery(qs: URLSearchParams): EstadoAsociado | '' {
  const raw = (qs.get('estado') ?? '').toLowerCase()
  if (VALID_ESTADOS.has(raw as EstadoAsociado)) return raw as EstadoAsociado
  return ''
}
void parseEstadoFromQuery  // evitar warning si queda sin uso local

export function NeoCoreAsociadosPage() {
  const [searchParams, setSearchParams] = useSearchParams()

  const [rows, setRows] = useState<AsociadoNeoRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [page, setPage] = useState(0)
  const [total, setTotal] = useState(0)
  const [pendientesKyc, setPendientesKyc] = useState(0)
  const [counts, setCounts] = useState<Record<TabKey, number>>({
    todos: 0, nuevos: 0, activos: 0, suspendidos: 0, retirados: 0,
  })

  const [search, setSearch] = useState('')
  const [frioOpen, setFrioOpen] = useState(false)

  // La tab activa reemplaza al viejo filtro de estado singular. El banner
  // "¡Un asociado envió su comprobante!" navega con ?tab=nuevos (o el legacy
  // ?estado=pendiente que también resuelve a 'nuevos').
  const [tab, setTab] = useState<TabKey>(() => parseTabFromQuery(searchParams))

  const [tipoPersona, setTipoPersona] = useState<TipoPersona | ''>('')
  const [requiereFeic, setRequiereFeic] = useState<'true' | 'false' | ''>('')

  // Default sort: primer_nombre asc — el padrón se opera alfabéticamente
  // (Nataly abre la app y busca a Ana, después Beatriz, etc.). Cuando llegue
  // uno nuevo, el banner arriba lo destaca; no hace falta que "burbujee"
  // por default en la lista.
  // Default sort inteligente por tab: en 'nuevos' (prospecto+pendiente) el
  // operador quiere ver primero los que llegaron recién — orden cronológico
  // reverso. En otras tabs el orden alfabético (por primer nombre) sigue
  // siendo lo natural para buscar.
  const [sortKey, setSortKey] = useState<SortKey>(
    tab === 'nuevos' ? 'created_at' : 'primer_nombre',
  )
  const [sortDir, setSortDir] = useState<SortDir>(
    tab === 'nuevos' ? 'desc' : 'asc',
  )

  const prevTabRef = useRef<TabKey>(tab)
  useEffect(() => {
    if (prevTabRef.current === tab) return
    prevTabRef.current = tab
    if (tab === 'nuevos') {
      setSortKey('created_at')
      setSortDir('desc')
    } else {
      setSortKey('primer_nombre')
      setSortDir('asc')
    }
    setPage(0)
  }, [tab])

  // Sincronizar la tab con la URL — bidireccional
  useEffect(() => {
    const fromUrl = parseTabFromQuery(searchParams)
    if (fromUrl !== tab) setTab(fromUrl)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams])

  const [filtersOpen, setFiltersOpen] = useState(false)
  const popoverRef = useRef<HTMLDivElement>(null)

  // Cmd+K abre el search
  const searchInputRef = useRef<HTMLInputElement>(null)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        searchInputRef.current?.focus()
      }
      if (e.key === 'Escape' && filtersOpen) {
        setFiltersOpen(false)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [filtersOpen])

  // Click fuera del popover lo cierra
  useEffect(() => {
    if (!filtersOpen) return
    const onClick = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setFiltersOpen(false)
      }
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [filtersOpen])

  // debounce search
  const [searchDebounced, setSearchDebounced] = useState('')
  useEffect(() => {
    const t = setTimeout(() => setSearchDebounced(search), 300)
    return () => clearTimeout(t)
  }, [search])

  // Carga de asociados según la tab activa. `TAB_TO_ESTADOS` traduce la tab
  // en el array de estados que espera el RPC. Cuando la tab es 'todos',
  // no pasamos filtro de estado.
  useEffect(() => {
    setLoading(true)
    setError(null)
    const estadosIn = TAB_TO_ESTADOS[tab]
    supabase
      .rpc('listar_asociados_v2', {
        p_estado: null,
        p_estados_in: estadosIn,
        p_tipo_persona: tipoPersona || null,
        p_requiere_feic: requiereFeic === '' ? null : requiereFeic === 'true',
        p_search: searchDebounced || null,
        // Tab "Nuevos": traemos TODO el set (sin paginar) para poder
        // agrupar en cliente — pendientes con comprobante arriba y
        // prospectos agrupados por fecha de ingreso. El volumen de
        // nuevos es chico (decenas), así que un limit alto es barato.
        p_limit: tab === 'nuevos' ? 500 : PAGE_SIZE,
        p_offset: tab === 'nuevos' ? 0 : page * PAGE_SIZE,
        p_sort_key: sortKey,
        p_sort_dir: sortDir,
      })
      .then(({ data, error }) => {
        if (error) {
          setError(error.message)
          setRows([])
          setTotal(0)
        } else {
          const list = (data ?? []) as AsociadoNeoRow[]
          setRows(list)
          setTotal(list[0]?.total_count ?? 0)
        }
        setLoading(false)
      })
  }, [tab, tipoPersona, requiereFeic, searchDebounced, page, sortKey, sortDir])

  // Conteos por estado para los badges de las tabs. Una sola RPC devuelve
  // los 6 conteos, así que no cargamos peso extra.
  useEffect(() => {
    supabase.rpc('contar_asociados_por_estado').then(({ data }) => {
      const row = (data as CountsRow[] | null)?.[0]
      if (!row) return
      setCounts({
        todos: Number(row.total ?? 0),
        nuevos: Number(row.prospecto ?? 0) + Number(row.pendiente ?? 0),
        activos: Number(row.activo ?? 0),
        suspendidos: Number(row.suspendido ?? 0),
        retirados: Number(row.retirado ?? 0),
      })
      setPendientesKyc(Number(row.prospecto ?? 0) + Number(row.pendiente ?? 0))
    })
  }, [tab])  // re-fetch cuando cambia la tab para reflejar mutaciones

  useEffect(() => {
    setPage(0)
  }, [tab, tipoPersona, requiereFeic, searchDebounced])

  // Reflejar la tab activa en la URL para que sean links compartibles y el
  // back/forward del browser funcione natural.
  useEffect(() => {
    const current = parseTabFromQuery(searchParams)
    if (current === tab) return
    const next = new URLSearchParams(searchParams)
    // Limpiar tanto tab como el legacy estado
    next.delete('estado')
    if (tab === 'todos') {
      next.delete('tab')
    } else {
      next.set('tab', tab)
    }
    setSearchParams(next, { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab])

  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / PAGE_SIZE)), [total])
  const activeFilters = useMemo(() => {
    // Nota: la tab activa NO se lista como "filtro activo" porque ya está
    // visible arriba como pill; solo mostramos los filtros secundarios.
    const out: Array<{ key: string; label: string; clear: () => void }> = []
    if (tipoPersona) out.push({ key: 'tipo', label: `Tipo: ${capitalize(tipoPersona)}`, clear: () => setTipoPersona('') })
    if (requiereFeic === 'true') out.push({ key: 'feic', label: 'Requiere FEIC', clear: () => setRequiereFeic('') })
    if (requiereFeic === 'false') out.push({ key: 'feic', label: 'Solo FEIS', clear: () => setRequiereFeic('') })
    return out
  }, [tipoPersona, requiereFeic])

  const clearAllFilters = () => {
    setTab('todos')
    setTipoPersona('')
    setRequiereFeic('')
    setSearch('')
  }

  // Tab "Nuevos" = bandeja de triage (no tabla):
  //   1. "Listos para aprobar" (estado 'pendiente', ya subieron comprobante),
  //      FIFO — el que lleva más esperando primero.
  //   2. "Prospectos" (sin comprobante) agrupados por fecha de ingreso (GT).
  //   3. "En frío" = prospectos con más de EN_FRIO_DIAS sin comprobante,
  //      colapsados al fondo (no se borran, solo bajan de prioridad).
  const esNuevoTab = tab === 'nuevos'
  const bandeja = useMemo(() => {
    const pendientes = rows
      .filter((r) => r.estado_asociado === 'pendiente')
      .sort(byCreatedAsc)
    const prospectos = rows.filter((r) => r.estado_asociado === 'prospecto')
    const frescos: AsociadoNeoRow[] = []
    const frio: AsociadoNeoRow[] = []
    for (const r of prospectos) {
      if (diasDesde(r.fecha_ingreso ?? r.created_at) > EN_FRIO_DIAS) frio.push(r)
      else frescos.push(r)
    }
    frescos.sort(byCreatedDesc)
    frio.sort(byCreatedDesc)
    const groups = new Map<string, AsociadoNeoRow[]>()
    for (const r of frescos) {
      const k = dayKeyLocal(r.fecha_ingreso ?? r.created_at)
      const arr = groups.get(k)
      if (arr) arr.push(r)
      else groups.set(k, [r])
    }
    const dias = [...groups.entries()].sort((a, b) =>
      a[0] < b[0] ? 1 : a[0] > b[0] ? -1 : 0,
    )
    return { pendientes, dias, frio }
  }, [esNuevoTab, rows])

  // Fila de asociado — reutilizable tanto en la vista plana (otras tabs)
  // como intercalada con headers de grupo (tab Nuevos).
  const renderAsociadoTr = (r: AsociadoNeoRow) => {
    const esNuevo = r.estado_asociado === 'prospecto' || r.estado_asociado === 'pendiente'
    return (
      <tr
        key={r.id}
        className="row-clickable"
        onClick={() => (window.location.href = `/neocore/asociados/${r.id}`)}
        style={
          esNuevo
            ? {
                background: 'rgba(241, 90, 36, 0.04)',
                borderLeft: '3px solid var(--brand-500, #FF5B04)',
              }
            : undefined
        }
      >
        <td>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div className="avatar-sm">{iniciales(r.nombre_completo)}</div>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <Link
                to={`/neocore/asociados/${r.id}`}
                style={{ color: 'var(--n-900)', fontWeight: 500 }}
                onClick={(e) => e.stopPropagation()}
              >
                {highlightMatch(r.nombre_completo, searchDebounced)}
              </Link>
              <span className="row-name-sub">DPI {fmtDpi(r.dpi)}</span>
            </div>
          </div>
        </td>
        <td className="num">
          <span className="mono val-strong">{r.neo_id || r.no_asociado}</span>
        </td>
        <td className="center">
          <EstadoBadge estado={r.estado_asociado} />
        </td>
        <td className="center">
          <ExpedienteBadges row={r} />
        </td>
        <td className="center">
          {r.ultimo_kyc_estado ? (
            <span className={`badge ${kycToneClass(r.ultimo_kyc_estado)}`}>
              {r.ultimo_kyc_estado}
            </span>
          ) : (
            <span style={{ color: 'var(--n-400)', fontSize: 12 }}>—</span>
          )}
        </td>
        <td className="num" style={{ color: 'var(--n-500)', fontSize: 12 }}>
          {fmtFechaCorta(r.fecha_ingreso ?? r.created_at)}
        </td>
      </tr>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* Header con stats inline */}
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        <div className="stats-inline">
          {esNuevoTab ? (
            <>
              <span className="stat-num" style={{ color: '#b45309' }}>{bandeja.pendientes.length}</span>
              <span className="stat-label">listo{bandeja.pendientes.length === 1 ? '' : 's'} para aprobar</span>
              <span className="stat-sep">·</span>
              <span className="stat-num">
                {(bandeja.dias.reduce((n, [, g]) => n + g.length, 0) + bandeja.frio.length).toLocaleString('es-GT')}
              </span>
              <span className="stat-label">prospecto{total === 1 ? '' : 's'} sin comprobante</span>
            </>
          ) : (
            <>
              <span className="stat-num">{total.toLocaleString('es-GT')}</span>
              <span className="stat-label">asociado{total === 1 ? '' : 's'}</span>
              {pendientesKyc > 0 && (
                <>
                  <span className="stat-sep">·</span>
                  <span className="stat-num" style={{ color: '#b45309' }}>{pendientesKyc}</span>
                  <span className="stat-label">por revisar</span>
                </>
              )}
            </>
          )}
        </div>
        <Link
          to="/neocore/asociar/nuevo"
          className="btn btn-primary"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, textDecoration: 'none' }}
        >
          <UserPlus size={14} weight="bold" />
          Nuevo asociado
        </Link>
      </div>

      {/* Tabs por estado — reemplaza el dropdown de estado escondido en el
          popover. Siempre visibles con conteo. La tab "Nuevos" combina
          prospecto + pendiente. */}
      <div
        role="tablist"
        style={{
          display: 'flex',
          gap: 4,
          padding: 4,
          background: 'var(--soft-gray)',
          borderRadius: 10,
          overflowX: 'auto',
          fontSize: 13,
        }}
      >
        {(['todos', 'nuevos', 'activos', 'suspendidos', 'retirados'] as TabKey[]).map((k) => {
          const active = tab === k
          const label =
            k === 'todos' ? 'Todos' :
            k === 'nuevos' ? 'Nuevos' :
            k === 'activos' ? 'Activos' :
            k === 'suspendidos' ? 'Suspendidos' : 'Retirados'
          const count = counts[k]
          const isPriority = k === 'nuevos' && count > 0
          return (
            <button
              key={k}
              role="tab"
              aria-selected={active}
              onClick={() => setTab(k)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                whiteSpace: 'nowrap',
                padding: '8px 14px',
                border: 'none',
                borderRadius: 8,
                cursor: 'pointer',
                fontWeight: 600,
                fontSize: 13,
                background: active ? 'white' : 'transparent',
                color: active
                  ? isPriority ? '#b45309' : 'var(--text-primary)'
                  : isPriority ? '#b45309' : 'var(--text-secondary)',
                boxShadow: active ? '0 1px 2px rgba(0,0,0,0.06)' : 'none',
              }}
            >
              <span>{label}</span>
              <span
                style={{
                  minWidth: 22,
                  padding: '1px 7px',
                  borderRadius: 999,
                  fontSize: 11,
                  fontWeight: 700,
                  fontVariantNumeric: 'tabular-nums',
                  background: isPriority
                    ? active ? 'rgba(241, 90, 36, 0.22)' : 'rgba(241, 90, 36, 0.15)'
                    : active ? 'var(--n-100, #eef1f4)' : 'rgba(0,0,0,0.06)',
                  color: isPriority ? '#b45309' : active ? 'var(--text-primary)' : 'var(--text-secondary)',
                }}
              >
                {count}
              </span>
            </button>
          )
        })}
      </div>

      {/* Search + Filtros */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <div className="search-input-wrap">
          <Search size={14} weight="bold" className="search-icon" />
          <input
            ref={searchInputRef}
            placeholder="Buscar por nombre, no. asociado o DPI…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <kbd>⌘ K</kbd>
        </div>

        <div className="filter-popover-wrap" ref={popoverRef}>
          <button
            className="btn btn-ghost"
            onClick={() => setFiltersOpen((v) => !v)}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
          >
            <SlidersHorizontal size={13} weight="bold" />
            Filtros
            {activeFilters.length > 0 && (
              <span
                style={{
                  background: 'var(--brand-500)',
                  color: 'white',
                  fontSize: 10,
                  fontWeight: 600,
                  borderRadius: 999,
                  padding: '0 6px',
                  marginLeft: 2,
                  minWidth: 16,
                  height: 16,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {activeFilters.length}
              </span>
            )}
          </button>

          {filtersOpen && (
            <div className="filter-popover" role="dialog" aria-label="Filtros">
              {/* El filtro de Estado se movió a las tabs siempre visibles arriba
                  de la tabla. Acá quedan solo los filtros secundarios. */}
              <label>
                Tipo de persona
                <select
                  value={tipoPersona}
                  onChange={(e) => setTipoPersona(e.target.value as TipoPersona | '')}
                >
                  <option value="">Individual y jurídica</option>
                  <option value="INDIVIDUAL">Individual</option>
                  <option value="JURIDICA">Jurídica</option>
                </select>
              </label>
              <label>
                Expediente
                <select
                  value={requiereFeic}
                  onChange={(e) => setRequiereFeic(e.target.value as 'true' | 'false' | '')}
                >
                  <option value="">Todos</option>
                  <option value="true">Requieren FEIC</option>
                  <option value="false">Solo FEIS</option>
                </select>
              </label>
              <button
                className="btn btn-ghost"
                onClick={() => setFiltersOpen(false)}
                style={{ alignSelf: 'flex-end', fontSize: 12 }}
              >
                Listo
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Filter chips */}
      {activeFilters.length > 0 && (
        <div className="filter-chips">
          <span className="chips-label">Filtros activos:</span>
          {activeFilters.map((f) => (
            <span key={f.key} className="filter-chip">
              {f.label}
              <button onClick={f.clear} aria-label={`Quitar filtro ${f.label}`}>
                <X size={11} weight="bold" />
              </button>
            </span>
          ))}
          <button className="chips-clear" onClick={clearAllFilters}>
            Limpiar todos
          </button>
        </div>
      )}

      {error && (
        <div className="error-banner" style={{ margin: 0 }}>
          {error}
        </div>
      )}

      {/* Bandeja de Nuevos (triage) — reemplaza la tabla en este tab */}
      {esNuevoTab && (
        loading && rows.length === 0 ? (
          <div className="card" style={{ padding: 24, textAlign: 'center', color: 'var(--n-500)' }}>
            Cargando…
          </div>
        ) : (
          <BandejaNuevos
            pendientes={bandeja.pendientes}
            dias={bandeja.dias}
            frio={bandeja.frio}
            frioOpen={frioOpen}
            onToggleFrio={() => setFrioOpen((v) => !v)}
          />
        )
      )}

      {/* Tabla (todas las tabs menos Nuevos) */}
      {!esNuevoTab && (
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table className="data-table">
            <thead>
              <tr>
                <th>
                  <SortHeader
                    label="Nombre"
                    active={sortKey === 'primer_nombre'}
                    dir={sortDir}
                    onClick={() => toggleSort('primer_nombre', sortKey, sortDir, setSortKey, setSortDir)}
                  />
                </th>
                <th className="num">
                  <SortHeader
                    label="No. asociado"
                    active={sortKey === 'no_asociado'}
                    dir={sortDir}
                    onClick={() => toggleSort('no_asociado', sortKey, sortDir, setSortKey, setSortDir)}
                  />
                </th>
                <th className="center">Estado</th>
                <th className="center">Expediente</th>
                <th className="center">KYC</th>
                <th className="num">
                  <SortHeader
                    label="Ingresó"
                    active={sortKey === 'fecha_ingreso'}
                    dir={sortDir}
                    onClick={() => toggleSort('fecha_ingreso', sortKey, sortDir, setSortKey, setSortDir)}
                  />
                </th>
              </tr>
            </thead>
            <tbody>
              {loading && rows.length === 0 && (
                <SkeletonRows count={6} />
              )}

              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={6}>
                    <div className="empty-state">
                      <div className="empty-icon">
                        <UsersIcon size={20} weight="regular" />
                      </div>
                      <div className="empty-title">
                        {activeFilters.length > 0 || search
                          ? 'Ningún asociado coincide con los filtros'
                          : 'Aún no hay asociados registrados'}
                      </div>
                      <div>
                        {activeFilters.length > 0 || search
                          ? 'Probá con otros criterios o limpiá los filtros.'
                          : 'Empezá creando el primero desde el botón de arriba.'}
                      </div>
                    </div>
                  </td>
                </tr>
              )}

              {!loading && rows.map((r) => renderAsociadoTr(r))}
            </tbody>
          </table>
        </div>
      </div>
      )}

      {/* Pagination compacta numerada · oculta en tab Nuevos (viene sin paginar) */}
      {!esNuevoTab && total > 0 && (
        <div className="pagination">
          <span className="summary">
            Mostrando{' '}
            <strong>
              {(page * PAGE_SIZE + 1).toLocaleString('es-GT')}–
              {Math.min((page + 1) * PAGE_SIZE, total).toLocaleString('es-GT')}
            </strong>{' '}
            de <strong>{total.toLocaleString('es-GT')}</strong>
          </span>
          {totalPages > 1 && (
            <div className="pages">
              <button
                className="page-btn arrow"
                disabled={page === 0}
                onClick={() => setPage(0)}
                aria-label="Primera página"
              >
                «
              </button>
              <button
                className="page-btn arrow"
                disabled={page === 0}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                aria-label="Página anterior"
              >
                <ChevronLeft size={13} weight="bold" />
              </button>
              {pageNumbers(page, totalPages).map((n, i) =>
                n === '…' ? (
                  <span key={i} style={{ color: 'var(--n-400)', padding: '0 4px' }}>…</span>
                ) : (
                  <button
                    key={i}
                    className={'page-btn' + (n === page + 1 ? ' active' : '')}
                    onClick={() => setPage((n as number) - 1)}
                  >
                    {n}
                  </button>
                ),
              )}
              <button
                className="page-btn arrow"
                disabled={page + 1 >= totalPages}
                onClick={() => setPage((p) => p + 1)}
                aria-label="Página siguiente"
              >
                <ChevronRight size={13} weight="bold" />
              </button>
              <button
                className="page-btn arrow"
                disabled={page + 1 >= totalPages}
                onClick={() => setPage(totalPages - 1)}
                aria-label="Última página"
              >
                »
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// =============================================================
// Subcomponentes
// =============================================================

// =============================================================
// Bandeja de Nuevos (triage)
// =============================================================
function FlagChip({ label }: { label: string }) {
  return (
    <span
      style={{
        fontSize: 11,
        fontWeight: 600,
        color: '#b45309',
        background: 'rgba(241,90,36,0.10)',
        border: '0.5px solid #f0c9a8',
        borderRadius: 999,
        padding: '1px 8px',
      }}
    >
      ⚠ {label}
    </span>
  )
}

function SectionLabel({
  children, tone = 'neutral', count,
}: { children: ReactNode; tone?: 'brand' | 'neutral'; count?: number }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: 0.3,
        textTransform: 'uppercase',
        color: tone === 'brand' ? '#b45309' : 'var(--n-500)',
        padding: '4px 2px',
      }}
    >
      {tone === 'brand' && <span className="badge-live-dot" style={{ background: '#FF5B04' }} />}
      {children}
      {count != null && <span style={{ fontWeight: 500, color: 'var(--n-400)' }}>· {count}</span>}
    </div>
  )
}

function PendienteCard({ r }: { r: AsociadoNeoRow }) {
  const flags = flagsFor(r)
  const alerta = flags.length > 0
  return (
    <div
      className="card row-clickable"
      onClick={() => (window.location.href = `/neocore/asociados/${r.id}`)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: 14,
        borderLeft: `3px solid ${alerta ? '#dc2626' : '#FF5B04'}`,
        background: alerta ? 'rgba(220,38,38,0.04)' : 'rgba(241,90,36,0.04)',
      }}
    >
      <div className="avatar-sm" style={{ background: '#fde3d4', color: '#b45309' }}>
        {iniciales(r.nombre_completo)}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 500, color: 'var(--n-900)' }}>{r.nombre_completo}</span>
          {flags.map((f) => <FlagChip key={f} label={f} />)}
        </div>
        <div style={{ fontSize: 12, color: 'var(--n-500)', marginTop: 2 }}>
          Envió comprobante · {relTime(r.created_at)} · DPI {fmtDpi(r.dpi)}
        </div>
      </div>
      <span className="btn btn-primary" style={{ whiteSpace: 'nowrap' }}>Revisar aporte</span>
    </div>
  )
}

function ProspectoRow({ r }: { r: AsociadoNeoRow }) {
  const flags = flagsFor(r)
  return (
    <div
      className="row-clickable"
      onClick={() => (window.location.href = `/neocore/asociados/${r.id}`)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '9px 4px',
        borderTop: '0.5px solid var(--n-100, #eee)',
      }}
    >
      <div className="avatar-sm" style={{ width: 30, height: 30, fontSize: 11 }}>
        {iniciales(r.nombre_completo)}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 13, color: 'var(--n-900)' }}>{r.nombre_completo}</span>
          {flags.map((f) => <FlagChip key={f} label={f} />)}
        </div>
        <div style={{ fontSize: 11, color: 'var(--n-400)', marginTop: 1 }}>
          DPI {fmtDpi(r.dpi)} · {relTime(r.created_at)}
        </div>
      </div>
      <ChevronRight size={14} weight="bold" style={{ color: 'var(--n-300)' }} />
    </div>
  )
}

function BandejaNuevos({
  pendientes, dias, frio, frioOpen, onToggleFrio,
}: {
  pendientes: AsociadoNeoRow[]
  dias: Array<[string, AsociadoNeoRow[]]>
  frio: AsociadoNeoRow[]
  frioOpen: boolean
  onToggleFrio: () => void
}) {
  const vacio = pendientes.length === 0 && dias.length === 0 && frio.length === 0
  if (vacio) {
    return (
      <div className="card" style={{ padding: 32, textAlign: 'center' }}>
        <div className="empty-title">No hay asociados nuevos</div>
        <div style={{ color: 'var(--n-500)', fontSize: 13 }}>
          Cuando alguien se registre o envíe su comprobante, aparece acá.
        </div>
      </div>
    )
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      {pendientes.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <SectionLabel tone="brand" count={pendientes.length}>Listos para aprobar</SectionLabel>
          {pendientes.map((r) => <PendienteCard key={r.id} r={r} />)}
        </div>
      )}

      {dias.length > 0 && (
        <div className="card" style={{ padding: '6px 14px 10px' }}>
          <SectionLabel>Prospectos · sin comprobante</SectionLabel>
          {dias.map(([key, grupo]) => (
            <div key={key}>
              <div style={{ fontSize: 11, color: 'var(--n-400)', padding: '8px 2px 2px' }}>
                {dayLabel(key)} · {grupo.length}
              </div>
              {grupo.map((r) => <ProspectoRow key={r.id} r={r} />)}
            </div>
          ))}
        </div>
      )}

      {frio.length > 0 && (
        <div className="card" style={{ padding: '6px 14px 10px' }}>
          <button
            onClick={onToggleFrio}
            className="row-clickable"
            style={{
              display: 'flex', alignItems: 'center', gap: 8, width: '100%',
              background: 'none', border: 'none', cursor: 'pointer', padding: '6px 2px',
            }}
          >
            <ChevronRight
              size={13}
              weight="bold"
              style={{ transform: frioOpen ? 'rotate(90deg)' : 'none', color: 'var(--n-400)' }}
            />
            <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: 0.3, textTransform: 'uppercase', color: 'var(--n-500)' }}>
              En frío · +{EN_FRIO_DIAS} días sin comprobante
            </span>
            <span style={{ fontWeight: 500, color: 'var(--n-400)', fontSize: 11 }}>· {frio.length}</span>
          </button>
          {frioOpen && frio.map((r) => <ProspectoRow key={r.id} r={r} />)}
        </div>
      )}
    </div>
  )
}

function SortHeader({
  label, active, dir, onClick,
}: { label: string; active: boolean; dir: SortDir; onClick: () => void }) {
  return (
    <span className={'sort-th' + (active ? ' sort-active' : '')} onClick={onClick}>
      {label}
      {active ? (
        <ChevronDown
          size={11}
          weight="bold"
          style={{ transform: dir === 'asc' ? 'rotate(180deg)' : 'none' }}
        />
      ) : (
        <ChevronsUpDown size={11} weight="bold" />
      )}
    </span>
  )
}

function EstadoBadge({ estado }: { estado: EstadoAsociado }) {
  if (estado === 'activo') {
    return (
      <span className="badge badge-success">
        <span className="badge-live-dot" />
        Activo
      </span>
    )
  }
  return (
    <span className={`badge ${estadoToneClass(estado)}`}>
      {capitalize(estado)}
    </span>
  )
}

function ExpedienteBadges({ row }: { row: AsociadoNeoRow }) {
  return (
    <span style={{ display: 'inline-flex', gap: 4, justifyContent: 'center' }}>
      <span className={`badge ${row.tiene_feis ? 'badge-success' : 'badge-muted'}`}>
        {row.tiene_feis ? 'FEIS' : 'Sin FEIS'}
      </span>
      {row.requiere_feic && (
        <span className={`badge ${row.tiene_feic ? 'badge-info' : 'badge-warn'}`}>
          {row.tiene_feic ? 'FEIC' : 'FEIC pendiente'}
        </span>
      )}
    </span>
  )
}

function SkeletonRows({ count }: { count: number }) {
  return (
    <>
      {Array.from({ length: count }).map((_, i) => (
        <tr key={i} style={{ background: i % 2 === 0 ? 'transparent' : 'rgba(241,90,36,0.02)' }}>
          <td>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div className="skel" style={{ width: 28, height: 28, borderRadius: '50%' }} />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <div className="skel" style={{ width: 160, height: 12 }} />
                <div className="skel" style={{ width: 110, height: 10 }} />
              </div>
            </div>
          </td>
          <td className="num"><div className="skel" style={{ width: 80, height: 12, marginLeft: 'auto' }} /></td>
          <td className="center"><div className="skel" style={{ width: 60, height: 18, borderRadius: 999, margin: '0 auto' }} /></td>
          <td className="center"><div className="skel" style={{ width: 90, height: 18, borderRadius: 999, margin: '0 auto' }} /></td>
          <td className="center"><div className="skel" style={{ width: 50, height: 18, borderRadius: 999, margin: '0 auto' }} /></td>
          <td className="num"><div className="skel" style={{ width: 40, height: 11, marginLeft: 'auto' }} /></td>
        </tr>
      ))}
    </>
  )
}

// =============================================================
// helpers
// =============================================================

function toggleSort(
  k: SortKey, current: SortKey, dir: SortDir,
  setKey: (v: SortKey) => void, setDir: (v: SortDir) => void,
) {
  if (current === k) {
    setDir(dir === 'asc' ? 'desc' : 'asc')
  } else {
    setKey(k)
    setDir('desc')
  }
}

/** Orden por created_at descendente (más reciente primero). */
function byCreatedDesc(a: AsociadoNeoRow, b: AsociadoNeoRow): number {
  return (b.created_at ?? '').localeCompare(a.created_at ?? '')
}

/** Orden por created_at ascendente (FIFO: el más viejo primero). */
function byCreatedAsc(a: AsociadoNeoRow, b: AsociadoNeoRow): number {
  return (a.created_at ?? '').localeCompare(b.created_at ?? '')
}

/** Clave de día 'YYYY-MM-DD' en hora local (GT), no UTC. */
function dayKeyLocal(iso: string | null | undefined): string {
  if (!iso) return ''
  const d = new Date(iso.length === 10 ? `${iso}T12:00:00` : iso)
  if (isNaN(d.getTime())) return iso.slice(0, 10)
  return localDayKey(d)
}

/** Días transcurridos desde una fecha (local). */
function diasDesde(iso: string | null | undefined): number {
  if (!iso) return 0
  const d = new Date(iso.length === 10 ? `${iso}T12:00:00` : iso)
  if (isNaN(d.getTime())) return 0
  return Math.floor((Date.now() - d.getTime()) / 86400000)
}

/** "recién" / "hace 3 h" / "hace 2 d". */
function relTime(iso: string | null | undefined): string {
  if (!iso) return ''
  const min = Math.floor((Date.now() - new Date(iso).getTime()) / 60000)
  if (min < 1) return 'recién'
  if (min < 60) return `hace ${min} min`
  const h = Math.floor(min / 60)
  if (h < 24) return `hace ${h} h`
  const d = Math.floor(h / 24)
  return `hace ${d} día${d === 1 ? '' : 's'}`
}

/** Etiquetas de banderas de cumplimiento del asociado. */
function flagsFor(r: AsociadoNeoRow): string[] {
  const f: string[] = []
  if (r.aporte_alerta) f.push('Comprobante')
  if (r.recibo_alerta) f.push('Recibo')
  if (r.doc_alerta) f.push('DPI')
  if (r.pep_alerta) f.push('PEP')
  return f
}

/** 'YYYY-MM-DD' local de una fecha. */
function localDayKey(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

/** Etiqueta humana de un día: Hoy / Ayer / "mar 12 ago". */
function dayLabel(key: string): string {
  if (!key) return 'Sin fecha'
  const now = new Date()
  const hoy = localDayKey(now)
  const ayer = localDayKey(new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1))
  if (key === hoy) return 'Hoy'
  if (key === ayer) return 'Ayer'
  const d = new Date(`${key}T12:00:00`)
  if (isNaN(d.getTime())) return key
  return d.toLocaleDateString('es-GT', { weekday: 'short', day: '2-digit', month: 'short' })
}

function pageNumbers(current: number, total: number): Array<number | '…'> {
  // current es 0-indexed, devolvemos 1-indexed
  const c = current + 1
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1)
  if (c <= 4) return [1, 2, 3, 4, 5, '…', total]
  if (c >= total - 3) return [1, '…', total - 4, total - 3, total - 2, total - 1, total]
  return [1, '…', c - 1, c, c + 1, '…', total]
}

function highlightMatch(text: string, query: string) {
  if (!query) return text
  const q = query.trim()
  if (!q) return text
  const idx = text.toLowerCase().indexOf(q.toLowerCase())
  if (idx < 0) return text
  return (
    <>
      {text.slice(0, idx)}
      <mark className="search-highlight">{text.slice(idx, idx + q.length)}</mark>
      {text.slice(idx + q.length)}
    </>
  )
}

/**
 * Iniciales = primera letra del primer nombre + primera letra del primer apellido.
 * Filtra preposiciones cortas comunes en apellidos compuestos (DE, DEL, LA, etc).
 * Convención cooperativa GT: nombres + apellidos (típicamente 2 nombres + 2 apellidos).
 */
function iniciales(nombre: string | null | undefined): string {
  if (!nombre) return '—'
  const STOP = new Set(['DE', 'DEL', 'LA', 'LAS', 'LOS', 'Y', 'DA', 'DI', 'VAN', 'VON'])
  const parts = nombre
    .trim()
    .toUpperCase()
    .split(/\s+/)
    .filter((p) => p && !STOP.has(p))
  if (parts.length === 0) return '—'
  if (parts.length === 1) return parts[0].slice(0, 2)
  if (parts.length <= 3) {
    // 2 palabras: nombre + apellido. 3 palabras: nombre + 2 apellidos → primer apellido = parts[1].
    return parts[0][0] + parts[1][0]
  }
  // 4+: asumir 2 nombres + 2 apellidos (o más). El primer apellido es la palabra del medio.
  const firstApellidoIdx = Math.floor(parts.length / 2)
  return parts[0][0] + parts[firstApellidoIdx][0]
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase()
}

function fmtDpi(dpi: string | null | undefined): string {
  if (!dpi) return '—'
  if (dpi.length !== 13) return dpi
  return `${dpi.slice(0, 4)} ${dpi.slice(4, 9)} ${dpi.slice(9)}`
}

function fmtFechaCorta(iso: string | null): string {
  if (!iso) return '—'
  try {
    // DATE 'YYYY-MM-DD': anclar mediodía para no correrse un día en GT (UTC-6)
    const d = new Date(iso.length === 10 ? iso + 'T12:00:00' : iso)
    return d.toLocaleDateString('es-GT', { month: 'short', day: 'numeric' })
  } catch { return iso }
}

function estadoToneClass(estado: EstadoAsociado): string {
  switch (estado) {
    case 'activo':     return 'badge-success'
    case 'pendiente':  return 'badge-warn'
    case 'prospecto':  return 'badge-info'
    case 'suspendido': return 'badge-danger'
    case 'retirado':   return 'badge-muted'
    default:           return 'badge-muted'
  }
}

function kycToneClass(estado: KycEstado): string {
  switch (estado) {
    case 'pass':      return 'badge-success'
    case 'review':    return 'badge-warn'
    case 'fail':      return 'badge-danger'
    case 'expirado':  return 'badge-danger'
    default:          return 'badge-info'
  }
}
